import { Link } from "react-router-dom";
import { Briefcase, ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import Seo, { SITE } from "@/components/Seo";
import { useContent } from "@/context/ContentContext";

export default function Work() {
  const { portfolio: p = {} } = useContent();
  let src = p.gammaUrl || "";
  if (src.includes("gamma.app/docs/")) src = src.replace("gamma.app/docs/", "gamma.app/embed/");

  return (
    <div className="pt-20">
      <Seo
        title="Portfolio | Kim Kornachuk | Marketing Work That Shipped"
        description="Selected marketing work by Kim Kornachuk: website rebuilds, global product launches, competitive programs, and AI-assisted GTM projects across B2B SaaS and enterprise."
        path="/work"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Kim Kornachuk | Portfolio",
          url: `${SITE}/work`,
          author: { "@type": "Person", name: "Kim Kornachuk", url: SITE },
        }}
      />
      <section className="relative overflow-hidden">
        <div className="absolute inset-y-0 left-0 w-1/2 orange-wash opacity-50 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 sm:px-10 pt-16 sm:pt-24 pb-12 relative">
          <Reveal>
            <p className="font-mono-brand text-xs uppercase tracking-[0.25em] text-[var(--brand-primary)] mb-6 t-heading">{p.kicker}</p>
            <h1 className="font-display text-6xl sm:text-8xl leading-[0.92] tracking-tight max-w-4xl whitespace-pre-line t-heading">{p.heading}</h1>
            <p className="mt-6 text-lg text-[var(--brand-muted)] font-medium max-w-2xl t-body" data-testid="work-intro">{p.intro}</p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-10 pb-24">
        {src ? (
          <Reveal>
            <div className="bg-[#FBF9F4] border border-[#E7DFD0] rounded-3xl shadow-[0_8px_30px_-14px_rgba(28,24,21,0.16)] p-2 sm:p-3">
              <iframe
                src={src}
                title="Kim Kornachuk Portfolio"
                data-testid="work-embed"
                className="w-full rounded-2xl"
                style={{ height: "85vh", minHeight: "640px", border: 0 }}
                allow="fullscreen"
              />
            </div>
          </Reveal>
        ) : (
          <Reveal>
            <div className="bg-[#FBF9F4] border border-[#E7DFD0] rounded-3xl shadow-[0_8px_30px_-14px_rgba(28,24,21,0.16)] p-10 sm:p-14 text-center" data-testid="work-unavailable">
              <Briefcase size={44} className="text-[var(--brand-primary)] mx-auto" />
              <h2 className="font-display text-3xl sm:text-4xl tracking-tight mt-5">Portfolio coming online.</h2>
              <p className="mt-4 text-[var(--brand-muted)] font-medium max-w-xl mx-auto">The full portfolio is being polished. In the meantime, the track record lives on the resume page.</p>
              <Link to="/resume" data-testid="work-resume-cta" className="group mt-8 inline-flex items-center gap-2 bg-[var(--brand-primary)] text-[var(--brand-cream)] px-7 py-4 rounded-full font-bold uppercase tracking-[0.1em] text-sm transition-all hover:bg-[var(--brand-primary-dark)] shadow-[0_10px_30px_-10px_rgba(225,37,27,0.6)]">
                View resume <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        )}
      </section>
    </div>
  );
}
